import React, { useEffect, useRef, useState } from 'react';
import { Send, X, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import ChatMessageBubble from './ChatMessageBubble';
import './TutorChatPanel.css';

// Individual student chat tile
function StudentChatTile({ student, messages, unreadCount, historyCount, onSend, onClose, onRead }) {
  const [input, setInput] = useState('');
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const bottomRef = useRef(null);
  const prevMessageCount = useRef(messages.length);

  useEffect(() => {
    if (historyCount > 0) {
      setHistoryVisible(true);
      const t = setTimeout(() => setHistoryVisible(false), 6000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (!collapsed) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, collapsed]);

  // Auto-expand when a new message arrives while collapsed
  useEffect(() => {
    if (collapsed && messages.length > prevMessageCount.current) {
      setCollapsed(false);
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  useEffect(() => { onRead?.(); }, []);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    onSend(text);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const firstName = student.studentName?.split(' ')[0] ?? student.studentName;

  return (
    <div className={`tutor-chat-tile ${collapsed ? 'tutor-chat-tile--collapsed' : ''}`}>
      <div className="tutor-chat-tile__header">
        {/* Left side: chevron + name + unread — clicking toggles collapse */}
        <button
          type="button"
          className="tutor-chat-tile__toggle"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? `Expand ${firstName}'s chat` : `Collapse ${firstName}'s chat`}
        >
          {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          <MessageSquare size={13} />
          <span>{firstName}</span>
          {unreadCount > 0 && (
            <span className="tutor-chat-tile__unread">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>

        {/* Right side: close confirmation or X */}
        {confirmingClose ? (
          <div className="tutor-chat-tile__confirm">
            <span>Close chat?</span>
            <button type="button" className="tutor-chat-tile__confirm-yes" onClick={onClose}>Yes</button>
            <button type="button" className="tutor-chat-tile__confirm-no" onClick={() => setConfirmingClose(false)}>No</button>
          </div>
        ) : (
          <button
            type="button"
            className="tutor-chat-tile__close-btn"
            onClick={(e) => { e.stopPropagation(); setConfirmingClose(true); }}
            aria-label={`Close ${firstName}'s chat`}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {!collapsed && (
        <>
          <div className="tutor-chat-tile__messages">
            {historyVisible && historyCount > 0 && (
              <div className="tutor-chat-tile__history-banner">
                {historyCount} previous message{historyCount > 1 ? 's' : ''}
              </div>
            )}
            {messages.length === 0 && (
              <p className="tutor-chat-tile__empty">No messages yet</p>
            )}
            {messages.map((msg, i) => (
              <ChatMessageBubble
                key={msg._id ?? `${msg.timestamp ?? i}-${i}`}
                message={msg.message}
                timestamp={msg.timestamp ?? msg.createdAt}
                isOwn={msg.senderType === 'tutor'}
              />
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="tutor-chat-tile__input-row">
            <textarea
              className="tutor-chat-tile__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${firstName}…`}
              maxLength={500}
              rows={1}
            />
            <button
              type="button"
              className="tutor-chat-tile__send"
              onClick={handleSend}
              disabled={!input.trim()}
              aria-label="Send"
            >
              <Send size={13} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function TutorChatPanel({
  students,              // Array<{ studentId, studentName, sessionId }>
  messagesByStudent,     // Record<studentId, ChatMessage[]>
  unreadByStudent,       // Record<studentId, number>
  historyCountByStudent, // Record<studentId, number>
  onSend,                // (studentId, sessionId, text) => void
  onRead,                // (studentId) => void
  onClose,               // () => void
  onCloseStudent,        // (studentId) => void
}) {
  return (
    <div className="tutor-chat-panel">
      <div className="tutor-chat-panel__header">
        <span>Chat</span>
        <button type="button" className="tutor-chat-panel__close" onClick={onClose} aria-label="Close chat panel">
          <X size={16} />
        </button>
      </div>

      <div className="tutor-chat-panel__tiles">
        {students.length === 0 && (
          <p className="tutor-chat-panel__empty">No students in call</p>
        )}
        {students.map((s) => (
          <StudentChatTile
            key={s.studentId}
            student={s}
            messages={messagesByStudent[s.studentId] ?? []}
            unreadCount={unreadByStudent[s.studentId] ?? 0}
            historyCount={historyCountByStudent[s.studentId] ?? 0}
            onSend={(text) => onSend(s.studentId, s.sessionId, text)}
            onClose={() => onCloseStudent(s.studentId)}
            onRead={() => onRead(s.studentId)}
          />
        ))}
      </div>
    </div>
  );
}
